import { FC, useEffect, useRef, useState } from 'react';

import './CustomerDisplay.css';
import { DisplayCart, displayStreamUrl, openDisplaySession, resolveImageSrc } from '../../api/client';
import { formatDollars } from '../../shared/functions/formatter';
import { Button, Notice, StatusBadge } from '../../ui';
import { renderPaynowQr } from './paynowRender';

type CustomerDisplayProps = {
  onExit: () => void;
};

const EMPTY_CART: DisplayCart = {
  items: [],
  total: 0,
  savings: 0,
  event_name: null,
  order_complete: false,
  updated_at: null,
  paynow: null,
};

// Staff leave by holding the header; short taps do nothing.
const HOLD_TO_EXIT_MS = 900;
// How long the thank-you stays up before the screen returns to its empty state.
const COMPLETE_VISIBLE_MS = 8000;
// A QR render that never settles (a stalled logo fetch, a hung canvas) would leave
// the customer on "preparing..." forever, because nothing throws. Bound it and fall
// through to the same warning the failure path already shows.
const QR_RENDER_TIMEOUT_MS = 3000;
// The display's own credential expires within the hour, so re-mint it well
// inside that window: a stream that stays up for a whole shift must still hold a
// valid cookie when it next blips.
const DISPLAY_SESSION_REFRESH_MS = 30 * 60 * 1000;
// EventSource reports an error on every failed reconnect, so keep a burst of
// them (the server is down, the network is offline) from hammering the endpoint.
const DISPLAY_SESSION_REFRESH_FLOOR_MS = 60 * 1000;
// The first stream request has to carry a display cookie, so the mount waits for
// a mint before opening it. The wait is bounded: a mint that never answers opens
// the stream anyway, which then re-mints from its own error path.
const DISPLAY_SESSION_OPEN_TIMEOUT_MS = 5000;

/**
 * The customer-facing mirror of the till.
 *
 * One SSE connection redraws the whole basket on every frame, so it stays in
 * step with the cashier's iPad without polling. Read from across a booth, so the
 * layout is two clear halves - what was bought on the left, what is owed and how
 * to pay it on the right - with the amount sitting directly above the code.
 *
 * This is deliberately not an operator surface: it has no controls, and the only
 * way out (hold the header, or Escape) is hidden from the customer.
 *
 * There is no payment confirmation in the system - the server builds a static
 * PayNow code from the total - so the screen never claims a payment was made.
 */
const CustomerDisplay: FC<CustomerDisplayProps> = ({ onExit }) => {
  const [cart, setCart] = useState<DisplayCart>(EMPTY_CART);
  const [live, setLive] = useState(false);
  // The till's explicit "order finished" flag, shown for a short window. The
  // expiry is keyed to the frame it arrived on, so the next basket clears it and
  // a new completion starts a fresh window - without setting state in an effect.
  const [expiredFor, setExpiredFor] = useState<string | null>(null);
  // The rendered code, kept with the key it came from, so a changed total can
  // never show the previous amount's QR.
  const [rendered, setRendered] = useState<{ key: string; url: string; failed: boolean } | null>(null);

  useEffect(() => {
    let cancelled = false;
    let source: EventSource | null = null;

    // The display cookie now carries a short-lived, display-scoped token rather
    // than the session, so it has to be renewed for the stream to outlive it.
    // EventSource cannot do that itself, so renew on mount, on every error (the
    // retry that follows is the one that needs the fresh cookie) and on a timer
    // comfortably inside the token's life. Best effort throughout: if the session
    // itself is gone the stream reports its own error.
    const mint = () =>
      openDisplaySession().catch(() => {
        // A tab that predates the sign-in cookie gets another chance on the
        // stream's next error, and again on the refresh timer.
      });
    const reMint = () => {
      void mint();
    };

    // A dropped stream fires an error on every failed reconnect, so the first
    // error renews immediately and later ones wait out a short floor rather than
    // hammering an endpoint the server is down for.
    let lastErrorRefresh = 0;
    const refreshAfterError = () => {
      const now = Date.now();
      if (now - lastErrorRefresh < DISPLAY_SESSION_REFRESH_FLOOR_MS) return;
      lastErrorRefresh = now;
      reMint();
    };

    const openStream = () => {
      if (cancelled || source !== null) return;
      const next = new EventSource(displayStreamUrl(), { withCredentials: true });
      source = next;
      next.addEventListener('open', () => setLive(true));
      next.addEventListener('error', () => {
        setLive(false);
        refreshAfterError();
      });
      next.addEventListener('cart', (event) => {
        try {
          setCart(JSON.parse((event as MessageEvent<string>).data) as DisplayCart);
        } catch {
          // A malformed frame is not worth breaking the display over; the next
          // basket replaces the whole thing.
        }
      });
    };

    // Wait for a fresh cookie before the first open, so the first request is the
    // authenticated one rather than a 401 followed by a retried reconnect. The
    // deadline stops a mint that never answers from holding the stream back
    // indefinitely; the error path above still re-mints and retries.
    const openDeadline = window.setTimeout(openStream, DISPLAY_SESSION_OPEN_TIMEOUT_MS);
    void mint().then(() => {
      window.clearTimeout(openDeadline);
      openStream();
    });

    const timer = window.setInterval(reMint, DISPLAY_SESSION_REFRESH_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.clearTimeout(openDeadline);
      source?.close();
    };
  }, []);

  useEffect(() => {
    if (!cart.order_complete || !cart.updated_at) return undefined;
    const at = cart.updated_at;
    // Only the timer touches state; the effect body itself does not.
    const timer = window.setTimeout(() => setExpiredFor(at), COMPLETE_VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [cart.order_complete, cart.updated_at]);

  const showComplete = cart.order_complete && expiredFor !== cart.updated_at;

  const paynow = cart.paynow;
  const renderKey = paynow ? [paynow.style, paynow.payload, paynow.logo_url ?? '', paynow.color ?? ''].join('|') : '';

  useEffect(() => {
    if (!paynow) return undefined;
    let cancelled = false;

    // The bound. A render that has not answered by then is treated exactly like one
    // that threw, so the customer gets the instruction rather than a spinner.
    const timer = window.setTimeout(() => {
      if (!cancelled) setRendered({ key: renderKey, url: '', failed: true });
    }, QR_RENDER_TIMEOUT_MS);

    renderPaynowQr(paynow)
      .then((url) => {
        if (!cancelled) {
          window.clearTimeout(timer);
          setRendered({ key: renderKey, url, failed: false });
        }
      })
      .catch((error: unknown) => {
        console.error('QR render failed', error);
        if (!cancelled) {
          window.clearTimeout(timer);
          setRendered({ key: renderKey, url: '', failed: true });
        }
      });

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [paynow, renderKey]);

  const current = paynow && rendered && rendered.key === renderKey ? rendered : null;
  const qr = current ? current.url : '';
  const qrReady = qr !== '';
  const qrFailed = current !== null && current.failed;

  // Staff exit. Escape for a keyboard, press-and-hold for a touch screen; the
  // customer never sees a button.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onExit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onExit]);

  const holdTimer = useRef<number | null>(null);
  const clearHold = () => {
    if (holdTimer.current !== null) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  };
  const startHold = () => {
    clearHold();
    holdTimer.current = window.setTimeout(onExit, HOLD_TO_EXIT_MS);
  };
  useEffect(() => clearHold, []);

  // Follow a new line, but never yank a customer who has scrolled up to read.
  const listRef = useRef<HTMLUListElement>(null);
  const userScrolled = useRef(false);
  const itemCount = cart.items.length;
  useEffect(() => {
    const list = listRef.current;
    if (!list || userScrolled.current) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    list.scrollTo({ top: list.scrollHeight, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [itemCount]);
  const rememberScroll = () => {
    userScrolled.current = true;
  };
  const settleScroll = () => {
    const list = listRef.current;
    if (!list) return;
    // Once they are back at the newest line, follow along again.
    if (list.scrollHeight - list.scrollTop - list.clientHeight < 48) userScrolled.current = false;
  };

  const empty = cart.items.length === 0;
  // Derived from the canonical total and savings, so the summary always adds up.
  const subtotal = cart.total + cart.savings;

  return (
    <div className="customerDisplay" data-live={live ? 'on' : 'off'}>
      <header
        className="customerHead"
        onPointerDown={startHold}
        onPointerUp={clearHold}
        onPointerLeave={clearHold}
        onPointerCancel={clearHold}
        onContextMenu={(event) => event.preventDefault()}
      >
        <div>
          <p className="customerEyebrow">your order</p>
          {cart.event_name && <p className="customerEvent">{cart.event_name}</p>}
        </div>
        <div className="customerHeadRight">
          {!live && (
            <span className="customerReconnecting" role="status">
              <StatusBadge tone="warning">reconnecting</StatusBadge>
            </span>
          )}
          <Button
            variant="secondary"
            size="sm"
            className="customerExit"
            onClick={onExit}
            aria-label="Exit the customer display"
            title="Exit the customer display (Esc)"
          >
            exit
          </Button>
        </div>
      </header>

      {showComplete && empty ? (
        /* The one acknowledgement after a recorded sale. It deliberately never
           says "paid": the system has no payment confirmation, only the till's
           explicit word that the order finished. */
        <div className="customerComplete" role="status">
          <p className="customerCompleteTitle">Thank you!</p>
          <p className="customerCompleteHint">Your order is complete.</p>
        </div>
      ) : empty ? (
        <div className="customerEmpty" role="status">
          <p className="customerEmptyTitle">Your order is empty</p>
          <p className="customerEmptyHint">Items will appear here as they are added.</p>
        </div>
      ) : (
        <div className="customerMain">
          <section className="customerOrder" aria-label="Your order">
            <ul className="customerItems" ref={listRef} onWheel={rememberScroll} onTouchMove={rememberScroll} onScroll={settleScroll}>
              {cart.items.map((line, index) => (
                <li className="customerItem" key={line.id ?? index}>
                  <span className="customerItemName">
                    <span className="customerQty">{line.quantity}×</span>
                    <span className="customerName">{line.title}</span>
                    <span className="customerUnit">{formatDollars(line.price)} each</span>
                  </span>
                  <span className="customerLine">{formatDollars(line.subtotal)}</span>
                </li>
              ))}
            </ul>

            <footer className="customerTotals">
              {cart.savings > 0 && (
                <>
                  <div className="customerTotalRow">
                    <span>subtotal</span>
                    <span>{formatDollars(subtotal)}</span>
                  </div>
                  <div className="customerTotalRow customerTotalRow--saving">
                    <span>discounts</span>
                    <span>−{formatDollars(cart.savings)}</span>
                  </div>
                </>
              )}
              <div className="customerTotal">
                <span>total</span>
                <span>{formatDollars(cart.total)}</span>
              </div>
            </footer>
          </section>

          <aside className="customerPaynow" aria-label="Payment">
            <p className="customerPayEyebrow">payment</p>
            <p className="customerPayAmount">
              <span className="customerPayVerb">pay</span>
              <span className="customerPayValue">{formatDollars(cart.total)}</span>
            </p>

            {paynow ? (
              <>
                <p className="customerPayInstruction">Scan with PayNow</p>
                <span className="customerQrWrap">
                  {qrReady ? (
                    <img
                      className="customerQr"
                      src={qr}
                      alt="PayNow QR code for this order"
                      // A data URL that fails to paint is the same user-visible
                      // outcome as a render that threw: no usable code, so the
                      // customer gets the warning rather than a blank square.
                      onError={() => setRendered({ key: renderKey, url: '', failed: true })}
                    />
                  ) : qrFailed ? null : (
                    // Never BOTH "preparing…" and "temporarily unavailable": in the
                    // failed state the warning below is the single instruction.
                    <span className="customerQr customerQrPlaceholder" aria-hidden="true">
                      preparing…
                    </span>
                  )}
                  {qrReady && paynow.style === 'logo' && paynow.logo_url && (
                    <img
                      className="customerQrLogo"
                      src={resolveImageSrc(paynow.logo_url)}
                      alt=""
                      // The overlay badge can fail on its own; the code beneath it
                      // is then unusable in practice, so it fails the same way.
                      onError={() => setRendered({ key: renderKey, url: '', failed: true })}
                    />
                  )}
                </span>
                {qrFailed ? (
                  <Notice variant="warning" className="customerPayNote">
                    PayNow is temporarily unavailable. Please ask the seller for assistance.
                  </Notice>
                ) : (
                  <span className="customerPayRecipient">
                    <span className="customerPayRecipientLabel">PayNow recipient</span>
                    <span className="customerPayName">{paynow.name}</span>
                    <span className="customerPayProxy">{paynow.proxy}</span>
                  </span>
                )}
              </>
            ) : cart.total > 0 ? (
              <Notice variant="warning" className="customerPayNote">
                PayNow is not set up for this account.
              </Notice>
            ) : (
              <Notice variant="info" className="customerPayNote">
                Nothing to pay.
              </Notice>
            )}
          </aside>
        </div>
      )}
    </div>
  );
};

export default CustomerDisplay;
